import { Box, Text, Tooltip } from '@mantine/core';
import { useId } from 'react';
import { Area, AreaChart } from 'recharts';
import { primaryBase } from '../../styles/design-tokens';

interface SparklinesProps {
  projectName?: string;
  width?: number;
  height?: number;
  showTotal?: boolean;
  data?: number[];
  isLoading?: boolean;
  error?: Error | null;
}

export function Sparklines({
  projectName,
  width = 100,
  height = 30,
  showTotal = true,
  data,
  isLoading = false,
  error = null,
}: SparklinesProps) {
  // Unique per-instance gradient id so multiple sparklines on one page don't
  // collide. Sanitize to SVG-id-safe characters and prefix with a letter so the
  // url(#id) reference always resolves, independent of React's useId() format.
  const gradientId = `sparkline-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  if (error) {
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    let displayMessage = 'Error';

    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      displayMessage = 'No access';
    } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      displayMessage = 'Not found';
    } else if (errorMessage.includes('project_ids')) {
      displayMessage = 'Invalid project';
    }

    return (
      <Box style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text size="xs" c="red" title={errorMessage}>
          {displayMessage}
        </Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text size="xs" c="dimmed">
          ...
        </Text>
      </Box>
    );
  }

  // Handle case where data is an array (direct sparkline data points)
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <Box style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text size="xs" c="dimmed">
            No data
          </Text>
        </Box>
      );
    }

    const total = data.reduce((sum, value) => sum + (value || 0), 0);

    return (
      <Tooltip
        label={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {projectName || 'Project'}: {total} changes
            </div>
            <div style={{ fontSize: '11px', opacity: 0.8 }}>Last 12 weeks</div>
          </div>
        }
        position="top"
        withArrow
        multiline
      >
        <Box
          style={{
            width,
            height: showTotal ? height + 20 : height,
          }}
        >
          {showTotal && (
            <Text size="xs" fw={600} c={primaryBase} mb={2}>
              {total.toLocaleString()}
            </Text>
          )}
          <AreaChart width={width} height={height} data={data.map((value) => ({ value }))}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryBase} stopOpacity={0.1} />
                <stop offset="100%" stopColor={primaryBase} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <Area
              type="linear"
              dataKey="value"
              stroke={primaryBase}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              isAnimationActive={false}
              connectNulls
              activeDot={false}
            />
          </AreaChart>
        </Box>
      </Tooltip>
    );
  }

  // No data case
  return (
    <Box style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Text size="xs" c="dimmed">
        No data
      </Text>
    </Box>
  );
}
